import { StyleSheet, css } from 'aphrodite';
import { type CSSProperties, type FC, type ReactNode, useEffect, useRef, useState } from 'react';
import type { CoachMessage } from '../ai';
import { PinnedCard, type SavedScroll } from './PinnedCard';

/** Tiles beyond this stay pinned but demote to chips; four is what fits at a readable width. */
export const MAX_VISIBLE_TILES = 4;

/**
 * Above this many tiles the Game Plan gives up its full-height column and the grid becomes an even
 * 2x2. Reps asked for the tall column while there is room to read everything at once, and for the
 * even split once the screen fills up.
 */
const COLLAPSE_AT = MAX_VISIBLE_TILES;

export type PinTile = {
	msg: CoachMessage;
	/**
	 * `lead` is the Game Plan — the tile that holds the tall column until the grid collapses. `live` is
	 * the latest coaching suggestion; the parent puts it first during a call and last otherwise.
	 */
	kind: 'lead' | 'live' | 'pin';
	/** Live tile only: earlier suggestions, newest first, shown below the latest one. */
	feed?: CoachMessage[];
	/** Live tile only: header controls such as "Get suggestion". */
	actions?: ReactNode;
	/** Live tile only: content under the suggestions, such as the transcript. */
	footer?: ReactNode;
	/** Stands in for a live suggestion that hasn't arrived yet, so it can't be dismissed. */
	placeholder?: boolean;
};

export type PinGridProps = {
	/** Visible order. The parent decides ordering; the grid only decides shape. */
	tiles: PinTile[];
	/** Put the first tile (the live one) in a full-height column beside the rest. */
	liveAside?: boolean;
	onUnpin: (messageId: string) => void;
	/** Bring a demoted (chip) tile back into the grid. */
	onPromote?: (messageId: string) => void;
	/** True while the AI is streaming, which drives the live tile's indicator. */
	isResponding?: boolean;
	wsConnected?: boolean;
};

/**
 * The pinned-tile grid in the middle of the focused view.
 *
 * Replaces the old single scrolling column, where the hero's 40vh cap plus a fixed-height Game Plan
 * left every other pin sharing one short well at the bottom — and three pins arrive on a normal call
 * before the rep touches anything.
 *
 * Shape is driven by how many tiles are showing, which is the pressure reps actually feel:
 *  - one tile fills the area,
 *  - two or three keep the Game Plan in a full-height left column with the rest stacked beside it,
 *  - four collapse to an even 2x2.
 * Anything past four demotes to a chip strip rather than shrinking every tile further. Each tile
 * scrolls internally, and any tile can be maximized to fill the grid for a long read.
 *
 * `data-layout` names the current shape so tests and debugging do not have to read grid CSS.
 */
export const PinGrid: FC<PinGridProps> = ({ tiles, liveAside, onUnpin, onPromote, isResponding, wsConnected }) => {
	const [maximizedId, setMaximizedId] = useState<string | null>(null);
	// Maximizing, and the live tile moving in and out of its call column, remount cards; this lets them reopen in place.
	const savedScrolls = useRef(new Map<string, SavedScroll>());
	useEffect(() => {
		const present = new Set(tiles.map(tileKey));
		for (const key of savedScrolls.current.keys()) {
			if (!present.has(key)) savedScrolls.current.delete(key);
		}
	}, [tiles]);

	const visible = tiles.slice(0, MAX_VISIBLE_TILES);
	const demoted = tiles.slice(MAX_VISIBLE_TILES);

	// A maximized tile that gets unpinned (or falls off on a lead switch) must not leave the grid stuck
	// showing nothing, so treat a missing id as not-maximized rather than tracking it in an effect.
	const maximized = visible.find(tile => tile.msg.id === maximizedId) ?? null;

	// The live column stays up while another tile is maximized so "Get suggestion" is always reachable mid-call.
	const liveSide = liveAside && visible[0]?.kind === 'live' && maximized?.kind !== 'live' ? visible[0] : null;
	const cells = maximized ? [maximized] : liveSide ? visible.slice(1) : visible;
	const shape: Shape = maximized ? 'maximized' : shapeFor(cells.length, Boolean(liveSide));

	const renderTile = (tile: PinTile) => {
		const isMaximized = tile === maximized;
		return (
			<PinnedCard
				key={tileKey(tile)}
				msg={tile.msg}
				elevated={tile.kind === 'lead'}
				live={tile.kind === 'live'}
				streaming={tile.kind === 'live' && isResponding}
				wsConnected={tile.kind === 'live' ? wsConnected : undefined}
				maximized={isMaximized}
				onToggleMaximize={tile.placeholder ? undefined : () => setMaximizedId(isMaximized ? null : tile.msg.id)}
				onUnpin={tile.placeholder ? undefined : () => onUnpin(tile.msg.id)}
				feed={tile.feed}
				actions={tile.actions}
				footer={tile.footer}
				emptyText={tile.placeholder ? 'Listening — suggestions will appear here.' : undefined}
				savedScroll={savedScrolls.current.get(tileKey(tile))}
				onScrollChange={scroll => savedScrolls.current.set(tileKey(tile), scroll)}
			/>
		);
	};

	// Every shape renders the same element tree and only the grid CSS changes. A differently shaped tree
	// remounts the Game Plan card, which throws away the rep's scroll position right as live coaching starts.
	return (
		<div
			className={css(styles.stack)}
			style={{ flexDirection: liveSide ? 'row' : 'column' }}
			data-layout={liveSide ? 'live-side' : shape}
		>
			<div className={css(styles.grid)} style={gridStyleFor(shape, cells.length, Boolean(liveSide))}>
				{cells.map((tile, index) => (
					<div key={tileKey(tile)} className={css(styles.cell)} style={cellPlacementFor(shape, index)}>
						{renderTile(tile)}
					</div>
				))}
				{shape === 'empty' && !liveSide && <EmptyPins />}
				{!maximized && <DemotedStrip tiles={demoted} onPromote={onPromote} />}
			</div>
			{liveSide && <div className={css(styles.cell, styles.liveColumn)}>{renderTile(liveSide)}</div>}
		</div>
	);
};

// The live slot keeps one key as suggestions replace each other, so its feed and transcript keep their scroll.
const tileKey = (tile: PinTile) => (tile.kind === 'live' ? 'live' : tile.msg.id);

type Shape = 'maximized' | 'empty' | 'single' | 'weighted' | 'even' | 'stacked';

// Beside the live column there's only half the width, so pins stack in one column instead of a 2x2.
const shapeFor = (count: number, besideLive: boolean): Shape => {
	if (count === 0) return 'empty';
	if (count === 1) return 'single';
	if (besideLive) return 'stacked';
	return count < COLLAPSE_AT ? 'weighted' : 'even';
};

// Inline rather than Aphrodite classes: Aphrodite injects a new class's rules a tick late, and a layout read in that
// gap sees an unconstrained grid, which silently clamps every card's scroll to the top.
const gridStyleFor = (shape: Shape, count: number, besideLive: boolean): CSSProperties => ({
	...gridTracksFor(shape, count),
	...(besideLive ? { paddingRight: 0 } : {}),
});

const ONE_TRACK = 'minmax(0, 1fr)';
// Two columns, never three: at 1900px the centre pane is about 1500px, so a third column drops each tile to roughly
// 490px, which is too narrow for the markdown these cards hold.
const TWO_TRACKS = 'repeat(2, minmax(0, 1fr))';

// The Game Plan leads the stacked column and gets twice the height of the pins under it.
const gridTracksFor = (shape: Shape, count: number): CSSProperties => {
	if (shape === 'weighted') {
		return { gridTemplateColumns: TWO_TRACKS, gridTemplateRows: `repeat(${count - 1}, minmax(0, 1fr))` };
	}
	if (shape === 'even') return { gridTemplateColumns: TWO_TRACKS, gridTemplateRows: TWO_TRACKS };
	if (shape === 'stacked') {
		return { gridTemplateColumns: ONE_TRACK, gridTemplateRows: `minmax(0, 2fr) repeat(${count - 1}, minmax(0, 1fr))` };
	}
	return { gridTemplateColumns: ONE_TRACK, gridTemplateRows: ONE_TRACK };
};

const cellPlacementFor = (shape: Shape, index: number): CSSProperties | undefined => {
	if (shape !== 'weighted') return undefined;
	return index === 0 ? { gridColumn: 1, gridRow: '1 / -1' } : { gridColumn: 2 };
};

/**
 * Tiles past the visible cap. Kept visible as chips so the rep can see what is still pinned and pull
 * one back, instead of it silently vanishing the way overflow did in the old scrolling column.
 */
const DemotedStrip: FC<{ tiles: PinTile[]; onPromote?: (messageId: string) => void }> = ({ tiles, onPromote }) => {
	if (tiles.length === 0) {
		return null;
	}

	return (
		<div className={css(styles.demotedStrip)} data-testid='demoted-pins'>
			<span className={css(styles.demotedLabel)}>ALSO PINNED</span>
			{tiles.map(tile => (
				<button
					key={tile.msg.id}
					type='button'
					className={css(styles.demotedChip)}
					onClick={() => onPromote?.(tile.msg.id)}
					title='Show this pin in the grid'
				>
					<span className={css(styles.demotedDot)} style={{ background: tile.msg.pinColor || 'var(--accent)' }} />
					{(tile.msg.pinnedKeyword || 'Pinned').toUpperCase()}
				</button>
			))}
		</div>
	);
};

const EmptyPins: FC = () => (
	<div className={css(styles.empty)}>Hover any AI message and click the pin icon to keep it here for the call.</div>
);

const styles = StyleSheet.create({
	stack: {
		flex: 1,
		minHeight: 0,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
	},
	liveColumn: {
		boxSizing: 'border-box',
		flex: '0 0 44%',
		padding: '16px 20px 16px 12px',
	},
	grid: {
		flex: 1,
		minHeight: 0,
		display: 'grid',
		gap: 10,
		padding: '14px 20px 16px',
	},
	cell: {
		display: 'flex',
		minHeight: 0,
		minWidth: 0,
	},
	demotedStrip: {
		gridColumn: '1 / -1',
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 6,
	},
	demotedLabel: {
		fontFamily: 'var(--font-mono)',
		fontSize: 9.5,
		fontWeight: 600,
		letterSpacing: '0.1em',
		color: 'var(--ink-faint)',
	},
	demotedChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontFamily: 'var(--font-mono)',
		fontSize: 9.5,
		fontWeight: 600,
		letterSpacing: '0.06em',
		padding: '4px 9px',
		borderRadius: 999,
		border: '1px solid var(--border)',
		background: 'var(--bg-elev)',
		color: 'var(--ink-muted)',
		cursor: 'pointer',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	demotedDot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
		flexShrink: 0,
	},
	empty: {
		alignSelf: 'flex-start',
		padding: '20px 16px',
		border: '1px dashed var(--border)',
		borderRadius: 10,
		textAlign: 'center',
		fontSize: 12.5,
		color: 'var(--ink-muted)',
		lineHeight: 1.5,
	},
});
